/**
 * Text Formatting Utilities
 *
 * Shared utility functions for text formatting across all chat channels.
 * These functions handle URL linkification, RTL text detection, and text direction.
 */

/**
 * Converts URLs in text to clickable HTML links
 *
 * @param text - The text containing URLs to linkify
 * @returns HTML string with clickable links
 *
 * @example
 * linkify("Visit www.example.com")
 * // Returns: 'Visit <a href="https://www.example.com" target="_blank">www.example.com</a>'
 */
export function linkify(text: string): string {
  const urlRegex = /((https?:\/\/|www\.)[^\s]+)/g;
  return text.replace(urlRegex, (url) => {
    let hyperlink = url;
    if (hyperlink.startsWith('www.')) {
      hyperlink = 'https://' + hyperlink; // Add https:// if starts with www
    }
    return `<a href="${hyperlink}" target="_blank" rel="noopener noreferrer" style="color: #0a84ff; text-decoration: underline;">${url}</a>`;
  });
}

/**
 * Escapes HTML special characters to prevent HTML rendering/parsing.
 */
export function escapeHtml(text: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Checks if a character is right-to-left (RTL)
 *
 * Detects RTL characters from Hebrew, Arabic, and other RTL scripts
 *
 * @param char - The character to check
 * @returns true if the character is RTL, false otherwise
 *
 * @example
 * isRtl('א') // Returns: true (Hebrew)
 * isRtl('a') // Returns: false (Latin)
 */
export function isRtl(char: string): boolean {
  const rtlCharExp = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
  return rtlCharExp.test(char);
}

/**
 * Determines text direction based on the first character
 *
 * Analyzes the first non-numeric character to determine if text should be
 * displayed right-to-left (RTL) or left-to-right (LTR)
 *
 * @param text - The text to analyze
 * @returns 'rtl' for right-to-left text, 'ltr' for left-to-right text
 *
 * @example
 * getDirection('Hello world') // Returns: 'ltr'
 * getDirection('שלום עולם') // Returns: 'rtl'
 * getDirection('123 test') // Returns: 'ltr' (skips numbers)
 */
export function getDirection(text: string): 'rtl' | 'ltr' {
  const firstChar = text.trim().charAt(0);
  if (firstChar) {
    if (isNaN(Number(firstChar))) {
      return isRtl(firstChar) ? 'rtl' : 'ltr';
    }
  }
  return 'ltr'; // default to ltr
}

/**
 * Converts basic markdown symbols to HTML tags
 *
 * Supported formatting:
 * - *text* -> <b>text</b> (Bold)
 * - _text_ -> <i>text</i> (Italic)
 * - ~text~ -> <s>text</s> (Strikethrough)
 *
 * @param text - The text containing markdown symbols
 * @returns HTML string with formatting tags
 */
export const markdownToHtml = (text: string) => {
  if (!text) return '';
  return escapeHtml(text)
    .replace(/\*(.*?)\*/g, '<b>$1</b>')
    .replace(/_(.*?)_/g, '<i>$1</i>')
    .replace(/~(.*?)~/g, '<s>$1</s>');
};

/**
 * Truncates text to a specified length and adds an ellipsis
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation (default: 60)
 * @returns Truncated text with ellipsis if length exceeds maxLength
 */
export const truncateText = (text: string, maxLength = 60) =>
  text?.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

/**
 * Strips HTML tags and their content (for style/script) from a string.
 *
 * @param html - The HTML string to strip
 * @returns Clean text string
 */
export function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // Remove <style> blocks and their content
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // Remove <script> blocks and their content
    .replace(/<[^>]*>/g, '') // Remove all remaining tags
    .replace(/&nbsp;/gi, ' ') // Replace &nbsp; with space
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&zwnj;/gi, '\u200c')
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
}
